/* eslint-disable prefer-promise-reject-errors */
import axios from 'axios'
import isArray from 'lodash/isArray'
import isObject from 'lodash/isObject'
import mapValues from 'lodash/mapValues'
import isNull from 'lodash/isNull'
import { stringify } from 'query-string'
import store from '@state/store'
import { i18n } from '@plugins/i18n'
import trim from 'lodash/trim'
import isString from 'lodash/isString'
import csvError from '@utils/csvError'

axios.defaults.baseURL = process.env.VUE_APP_API_BASE_URL

/**
 * replace all value null to string blank
 */
axios.interceptors.response.use((res) => {
  // Important: response interceptors **must** return the response.
  return transform(res, convertToBlank)
})

axios.interceptors.request.use((config) => {
  if (config.headers['content-type'] === 'application/json; utf-8') {
    config.data = JSON.stringify(
      transformRequest(JSON.parse(config.data), trim)
    )
  }
  return config
})

/**
 * @param {*} endpoint api url
 * @param {*} method http request method POST, PUT, GET, DELETE
 * @param {*} payload request body
 * @param {*} params request params
 * @returns Promise
 */

// TODO fix error undefined
const confirmed = store?.getters['confirm/errorConfirmed']

export const request = async ({
  endpoint,
  method = 'post',
  payload,
  params,
}) => {
  const query = params
    ? `?${stringify({
        ...params,
      })}`
    : ''
  const time = Date.now()
  try {
    store.commit('loading/SHOW_LOADING', time)
    const response = await axios({
      method,
      url: `${endpoint}${query}`,
      headers: {
        'content-type': 'application/json; utf-8',
      },
      ...(payload && { data: JSON.stringify(payload) }),
      withCredentials: true,
    })
    return handleResponse(response.data)
  } catch (error) {
    const serverDown = {
      msg: i18n.t('confirm.serverDown.msg'),
      title: i18n.t('confirm.serverDown.title'),
    }
    if (!confirmed) {
      store.commit('confirm/SET_GLOBAL_CONFIRM', serverDown)
    }
    return Promise((resolve, reject) => reject(serverDown))
  } finally {
    setTimeout(() => {
      store.commit('loading/HIDE_LOADING', time)
    }, 500)
  }
}

const handleResponse = (response = {}) =>
  new Promise((resolve, reject) => {
    const { code, msg, data } = response
    const commonError = {
      msg: i18n.t(`errors.${code}`) || msg,
      title: i18n.t('common.errorTitle'),
    }
    switch (code) {
      case 0:
        return resolve(data)
      case 52:
        // close other dialog error
        store.commit('confirm/RESET_GLOBAL_CONFIRM')
        commonError.title = i18n.t('common.errorTitleTimeOut')
        store.commit('confirm/SET_SERVER_RAN_OUT_CONFIRM', commonError)
        return reject(i18n.t(`errors.${code}`))
      case 131:
      case 132:
      case 51:
        if (response.msg === 'email is not a valid email type') {
          commonError.msg = 'email is not a valid email type'
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      case 200:
        return reject({
          agentCode: [i18n.t('agent.mail_exist')],
          agentCodeAdmin: [i18n.t('agent.exist')],
        })
      case 201:
        if (!confirmed) {
          commonError.msg = i18n.t('errors.201')
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      case 202:
        return reject({
          company: [i18n.t('agent.company_exist')],
        })
      case 220:
        if (!confirmed) {
          commonError.msg = i18n.t('device.501')
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject({})
      case 230:
        return reject({
          email: [i18n.t('agent.mail_exist')],
        })
      case 244:
        if (!confirmed) {
          commonError.msg = msg
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      case 500:
        if (!confirmed) {
          commonError.msg = csvError(msg)
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      case 400:
        if (!confirmed) {
          commonError.msg = csvError(msg)
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      case 410:
        if (!confirmed) {
          commonError.msg = msg
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      case 450:
        if (!confirmed) {
          commonError.msg = i18n.t('device.450')
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
      default:
        if (!confirmed) {
          store.commit('confirm/SET_GLOBAL_CONFIRM', commonError)
        }
        return reject(i18n.t(`errors.${code}`) || msg)
    }
  })

const transform = (obj, cb) => {
  if (isArray(obj)) {
    return obj.map((innerObj) => transform(innerObj, cb))
  } else if (isObject(obj)) {
    return mapValues(obj, (val) => transform(val, cb))
  } else if (isNull(obj)) {
    return cb()
  } else {
    return obj
  }
}

const convertToBlank = () => {
  return ''
}

const transformRequest = (obj, cb) => {
  if (isArray(obj)) {
    return obj.map((innerObj) => transformRequest(innerObj, cb))
  } else if (isObject(obj)) {
    return mapValues(obj, (val) => transformRequest(val, cb))
  } else if (isString(obj)) {
    return cb(obj)
  } else {
    return obj
  }
}

export const requestUpload = async (url, formData) => {
  axios.defaults.withCredentials = true
  try {
    const response = await axios({
      method: 'post',
      url: url,
      headers: {
        'content-type': 'multipart/form-data',
      },
      data: formData,
    })
    return handleResponse(response.data)
  } catch (error) {
    const serverDown = {
      msg: i18n.t('confirm.serverDown.msg'),
      title: i18n.t('confirm.serverDown.title'),
    }
    if (!confirmed) {
      store.commit('confirm/SET_GLOBAL_CONFIRM', serverDown)
    }
    return Promise((resolve, reject) => reject(serverDown))
  }
}
